import React, { useState } from 'react';
import styled from 'styled-components';
import { getProjectArt } from '../data/projectArt';
import { categoryTheme } from '../data/categoryTheme';

/**
 * The header band shared by every project card (catalogue grid, related-work
 * strips) and by the project-detail page: a painting under a Davinci-blue wash,
 * with the category pill (children) sitting on top.
 *
 * Design contract (approved treatment "B · Washed"):
 *  - The band has a FIXED height per variant, so a late-loading image can never
 *    shift the layout. The `<img>` is absolutely positioned inside it.
 *  - The wash is a real element stacked above the image, not a pseudo-element, so
 *    tests and print rules can address it. It exists to keep the pill and any
 *    text legible whatever the painting does.
 *  - The painting is decorative (`alt=""`): it illustrates an idea, and everything
 *    it could tell a reader is already written on the card.
 *  - A slug with no painting falls back to the plain blue header, so a project
 *    added to the catalogue before its art exists is not broken.
 *  - Print: the artwork and wash are dropped to save ink, and the band collapses
 *    to just the category pill.
 */
export type ProjectArtVariant = 'card' | 'mini' | 'detail';

interface VariantSpec {
  height: number;
  mobileHeight: number;
  /** Outer card radius minus its top border, so the band meets the corner cleanly. */
  radius: number;
  sizes: string;
}

const VARIANTS: Record<ProjectArtVariant, VariantSpec> = {
  card: {
    height: 170,
    mobileHeight: 170,
    radius: 8,
    // 1 column on phones, 2 beside the filter rail from 992px, 3 from 1700px.
    sizes: '(min-width: 1700px) 28vw, (min-width: 992px) 33vw, (min-width: 768px) 46vw, 100vw',
  },
  mini: {
    height: 120,
    mobileHeight: 120,
    radius: 7,
    sizes: '(min-width: 992px) 280px, (min-width: 520px) 46vw, 100vw',
  },
  detail: {
    height: 220,
    mobileHeight: 160,
    radius: 9,
    sizes: '(min-width: 860px) 770px, 100vw',
  },
};

const Band = styled.div<{ $spec: VariantSpec }>`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  height: ${({ $spec }) => $spec.mobileHeight}px;
  border-radius: ${({ $spec }) => $spec.radius}px ${({ $spec }) => $spec.radius}px 0 0;
  /* Today's plain blue header: the fallback, and what shows while the image loads. */
  background: linear-gradient(135deg, var(--davinci-blue-fill), var(--davinci-denim));

  @media (min-width: 768px) {
    height: ${({ $spec }) => $spec.height}px;
  }

  @media print {
    height: auto;
    background: none;
    overflow: visible;
  }
`;

const Painting = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  @media print {
    display: none;
  }
`;

/* 135deg, Davinci blue-fill into midnight: the same overlay the approved mock-up used. */
const Wash = styled.span`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(10, 125, 170, 0.55), rgba(20, 53, 98, 0.45));
  pointer-events: none;

  @media print {
    display: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  right: var(--spacing-md);
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);

  @media print {
    position: static;
    padding: var(--spacing-sm) 0;
  }
`;

export interface ProjectArtBandProps {
  slug: string;
  variant: ProjectArtVariant;
  /**
   * The band is above the fold (project-detail page): load the 800w file straight
   * away instead of lazily. Do NOT pair this with a `<link rel=preload>` in
   * index.html — that is on the SEO-PLAYBOOK do-not-undo list.
   */
  eager?: boolean;
  /** Render the plain blue band when the slug has no painting (cards do; the detail page does not). */
  showFallback?: boolean;
  /** Overlay content, e.g. the category pill. */
  children?: React.ReactNode;
  className?: string;
}

const ProjectArtBand: React.FC<ProjectArtBandProps> = ({
  slug,
  variant,
  eager = false,
  showFallback = true,
  children,
  className,
}) => {
  const art = getProjectArt(slug);
  // A painting that fails to load (missing file, blocked request) drops back to the
  // plain blue band instead of leaving the browser's broken-image icon in the corner.
  const [failed, setFailed] = useState(false);
  const painted = Boolean(art) && !failed;
  if (!painted && !showFallback) return null;
  const spec = VARIANTS[variant];

  return (
    <Band $spec={spec} className={className} data-project-art={painted ? 'painted' : 'plain'}>
      {art && !failed && (
        <>
          {/*
            PROP ORDER IS LOAD-BEARING. React applies attributes in the order the props
            are written, and a script-created <img> starts fetching the moment `src` is
            set. If `loading="lazy"` comes after `src`/`srcSet`, the request has already
            begun and lazy loading does nothing: a direct visit to /projects fetched all
            36 paintings at once (round 22 QA). Keep `loading` and `decoding` BEFORE
            `src`, `srcSet` and `sizes`.
          */}
          <Painting
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            alt=""
            width={art.width}
            height={art.height}
            sizes={spec.sizes}
            srcSet={`${art.src480} 480w, ${art.src800} 800w`}
            src={eager ? art.src800 : art.src480}
            onError={() => setFailed(true)}
          />
          <Wash data-project-art-wash aria-hidden="true" />
        </>
      )}
      {children && <Overlay>{children}</Overlay>}
    </Band>
  );
};

export default ProjectArtBand;

const Pill = styled.span<{ $ink: string }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  max-width: 100%;
  padding: 4px var(--spacing-md);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  /* Midnight, not the category ink: the pill sits on a painting, so its text takes
     the highest-contrast brand colour on white. The icon keeps the category ink. */
  color: var(--davinci-midnight);
  font-size: max(13px, var(--fs-xs));
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  line-height: 1.3;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    stroke-width: 2;
    color: ${({ $ink }) => $ink};
  }
`;

/** The category pill that sits on the band: icon plus the category written out in text. */
export const ProjectCategoryPill: React.FC<{ category: string }> = ({ category }) => {
  const theme = categoryTheme(category);
  const Icon = theme.icon;
  return (
    <Pill $ink={theme.ink}>
      <Icon aria-hidden="true" />
      {category}
    </Pill>
  );
};
